import { DEFAULT_SIZE, DOT_SHAPE, SOLID_LINE } from "src/blitz/Charts/constants"
import {
  assignAttributeStateToLegends,
  assignAttributeStateToTLLegends,
  convertTimeToMinutes,
  generateLegend,
  getCalculatedAndMinInterval,
  getChartObject,
  getMergedYAxisList,
  getPropertyAxisAndIsAdded,
  getTimeIndex,
  intervalToMinutes,
  legendIDGenerator,
  legendName
} from "../common"
import { isArray, isString } from "lodash"
import moment from "moment"
import {
  chartDateFormat,
  constantValues,
  reduxStoreTimeStampFormat,
  settingsReferenceLines
} from "../../constant"
import {
  TIMELINE_PROPERTY_TYPE,
  transformTimeLinechartNew
} from "./timeLineChart"
import {
  LINE_TYPE,
  REFERENCE_LINE_TYPE,
  CRITICAL_COLOR,
  CAUTIONARY_COLOR
} from "./lineChart"
import { generateExceptionBars } from "../Exceptions/index"
import { generateSuppressionBars } from "../Suppressions"
import { generateExceptionLegends } from "../Exceptions/helper"
import { generateSuppressionLegends } from "../Suppressions/helper"

export const transformLineWithBinaryStatesChart = ({
  tz,
  startDate,
  endDate,
  intervalMinutes = "15m",
  inputData,
  chartInfo,
  equipmentList,
  buildingId,
  equipmentId,
  addedProperties,
  oldLegends,
  oldTimeLineLegends = null,
  assignedAttributes = [],
  legendAttributesCombination,
  only15Min
}) => {
  let minimumCalculatedMin = intervalToMinutes(intervalMinutes)
  let calculatedMin = intervalToMinutes(intervalMinutes)
  let minInterval = calculatedMin
  const defaultCalculatedMin = intervalToMinutes(intervalMinutes)
  let lastIndexPosition = (60 / calculatedMin) * 24 - 1
  const legends = {}
  let timeLineLegends = {}
  const settings = inputData?.settings || {}
  const equipmentType = chartInfo?.equipmentType
  const addedAxisList = addedProperties?.axisList || []
  const data = inputData?.data || {}

  const newData = {}
  const allTimestampsObject = {}
  const localTimeStamp = Boolean(tz)
    ? moment(
        moment.tz(moment(), tz).format("MM-DD-YYYY HH:mm"),
        "MM-DD-YYYY HH:mm"
      )
    : moment()
  const endTimestamp = moment(endDate, chartDateFormat).add(1, "days")
  for (const id in data) {
    const paramData = data[id]
    const equipmentName = equipmentList?.find((obj) => obj?.id === id)?.name
    const selectedEquipment = equipmentId === id
    const building = buildingId === id
    const properties = paramData?.properties
    const legendEquipmentType = equipmentList?.find(
      (obj) => obj?.id === id
    )?.type
    const sortedDates = Object.keys(paramData?.values || {})
      .filter((date) => date != "frq")
      .sort(
        (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime()
      )

    for (const propertyKey in properties) {
      calculatedMin = defaultCalculatedMin
      minInterval = defaultCalculatedMin
      lastIndexPosition = (60 / minInterval) * 24 - 1
      const property = properties[propertyKey]
      const propertyIndex = property?.index
      const propertyName = property?.name || ""

      const { axis, isUserAddedProperty, legendId } = getPropertyAxisAndIsAdded(
        {
          chartInfo,
          propertyKey,
          equipmentType,
          equipmentList,
          id,
          addedAxisList
        }
      )
      let legendObj
      const name = legendName({
        building,
        selectedEquipment,
        propertyName,
        equipmentName
      })
      if (axis?.axisName === "timeline") {
        legendObj = generateLegend({
          legendId,
          name,
          axisName: axis?.axisName,
          symbol: axis?.symbol,
          propertyKey,
          type: "timeline",
          isUserAddedProperty,
          oldLegends: oldTimeLineLegends,
          propertyType: TIMELINE_PROPERTY_TYPE
        })
        timeLineLegends[legendId] = legendObj
      } else {
        legendObj = generateLegend({
          legendId,
          name,
          axisName: axis?.axisName,
          symbol: axis?.symbol,
          type: LINE_TYPE,
          isUserAddedProperty,
          legendEquipmentType,
          oldLegends
        })
        legends[legendId] = legendObj
      }

      let currentTimestamp = moment(startDate, chartDateFormat).locale("en")

      let prevValue = null
      while (
        currentTimestamp.isSameOrBefore(endTimestamp) &&
        currentTimestamp.isSameOrBefore(localTimeStamp)
      ) {
        const dateKey = currentTimestamp.format("YYYY-MM-DD")
        const extractTime = currentTimestamp.format("HH:mm")
        const timetoMin = convertTimeToMinutes(extractTime)

        const dateAvailable = paramData?.values?.[dateKey]
        if (dateAvailable) {
          const dateIndexPosition = sortedDates.indexOf(dateKey)
          const extractedInterval =
            paramData.values?.frq?.[dateIndexPosition]?.split("|")?.[
              propertyIndex
            ]
          const intervals = getCalculatedAndMinInterval({
            only15Min,
            axis,
            defaultCalculatedMin,
            extractedInterval,
            paramData,
            dateKey
          })
          calculatedMin = intervals.calculatedMin
          minInterval = intervals.minInterval
          lastIndexPosition = (60 / minInterval) * 24 - 1

          if (calculatedMin < minimumCalculatedMin) {
            minimumCalculatedMin = calculatedMin
          }
        }

        const timeIndexPosition = getTimeIndex(timetoMin, minInterval)

        let finalValue
        if (dateAvailable) {
          const valueArray = paramData?.values?.[dateKey]?.[timeIndexPosition]
          const valueArrayIsArray = isArray(valueArray)
          const arrayValues = valueArrayIsArray ? valueArray : []
          let value = arrayValues[propertyIndex]

          const isOutOfRange = value?.toString()?.startsWith("_")
          if (isOutOfRange && value?.toString()?.length > 1) {
            value = value?.toString()?.substring(1)
          }

          const checkedValue = constantValues?.includes(value) ? null : value
          if ((isString(value) && value?.length === 0) || !valueArrayIsArray) {
            finalValue = prevValue
          } else {
            finalValue = checkedValue
          }

          if (timeIndexPosition === lastIndexPosition) {
            prevValue = null
          } else {
            prevValue = finalValue
          }
        } else {
          finalValue = null
          prevValue = null
        }

        const dateVal = currentTimestamp
          .locale("en")
          .format(reduxStoreTimeStampFormat)

        if (!newData?.[legendId]) {
          newData[legendId] = {
            data: [],
            timestamps: []
          }
        }
        newData[legendId]?.data.push(finalValue)
        newData[legendId]?.timestamps.push(dateVal)
        if (!allTimestampsObject[dateVal]) {
          allTimestampsObject[dateVal] = 1
        }
        currentTimestamp = currentTimestamp.add(calculatedMin, "minutes")
      }
    }
  }
  const allTimestamps = Object.keys(allTimestampsObject).sort()
  const noDataArray = Object.keys(legends)?.map((key) => {
    const noData = (newData?.[key]?.data || [])?.every((e) => !Boolean(e))
    return noData
  })

  const isNoData = noDataArray?.every((e) => Boolean(e))

  Object.keys(settings)?.forEach((key) => {
    const axis = chartInfo?.axisConfig?.find((obj) =>
      getChartObject(obj, key, equipmentType)
    )
    const value = settings?.[key]
    const legendId = legendIDGenerator("", key)

    // Determine color based on limit type (critical = red, cautionary = yellow)
    const keyLower = key?.toLowerCase() || ""
    const isCritical = keyLower.includes("red") || keyLower.includes("critical")
    const isCautionary =
      keyLower.includes("yellow") || keyLower.includes("cautionary")
    const limitColor = isCritical
      ? CRITICAL_COLOR
      : isCautionary
      ? CAUTIONARY_COLOR
      : undefined

    const legendObj: any = generateLegend({
      legendId,
      name: settingsReferenceLines(key, chartInfo?.title || ""),
      axisName: axis?.axisName,
      symbol: axis?.symbol,
      type: REFERENCE_LINE_TYPE,
      value,
      oldLegends,
      additionalAttributes: limitColor ? { color: limitColor } : undefined
    })
    legends[key] = legendObj
  })

  assignAttributeStateToLegends(
    legends,
    oldLegends,
    assignedAttributes,
    legendAttributesCombination
  )

  // Create/update Exception Legends
  const exceptionData = inputData?.exceptions?.data || {}
  const exceptionLegends = generateExceptionLegends({
    exceptionData,
    chartInfo,
    equipmentList,
    selectedEquipID: equipmentId,
    oldTimeLineLegends
  })

  // Create/update Suppression Legends
  const suppressionData = inputData?.exceptions?.suppressed?.data || {}
  const suppressionLegends = generateSuppressionLegends({
    suppressionData,
    chartInfo,
    oldTimeLineLegends
  })

  // Assign Colors to new time line legends of all kinds
  assignAttributeStateToTLLegends(
    timeLineLegends,
    exceptionLegends,
    suppressionLegends,
    assignedAttributes,
    legendAttributesCombination,
    oldTimeLineLegends
  )

  const { timeLineBars, totalLanes } = transformTimeLinechartNew(
    timeLineLegends,
    newData
  )

  const { exceptionsBars, exceptionLanes } = generateExceptionBars(
    exceptionData,
    exceptionLegends,
    chartInfo,
    totalLanes,
    startDate,
    endTimestamp,
    localTimeStamp
  )

  const suppressed =
    (inputData?.exceptions?.suppressed?.data &&
      inputData?.exceptions?.suppressed?.data[equipmentId]) ||
    {}
  const { suppressionBars } = generateSuppressionBars(
    suppressed,
    suppressionLegends,
    exceptionLanes,
    startDate,
    endTimestamp.format("YYYY-MM-DD")
  )

  const bars = [...timeLineBars, ...exceptionsBars, ...suppressionBars]
  const isNoTimeLineData = bars?.every(
    (obj) => !Boolean(obj?.value) || obj?.value === "No Data"
  )

  timeLineLegends = {
    ...timeLineLegends,
    ...exceptionLegends,
    ...suppressionLegends
  }

  const axisList = getMergedYAxisList(chartInfo, addedAxisList)

  return {
    chartData: {
      data: isNoData && isNoTimeLineData ? {} : newData
    },
    chartProps: {
      legends: legends,
      timestamps: allTimestamps,
      axisList: axisList,
      timeLineLegends: timeLineLegends,
      timeLineBars: isNoTimeLineData && isNoData ? [] : bars,
      noDataAvailable: isNoData && isNoTimeLineData ? true : false,
      isdataprocessed: true,
      calculatedInterval: minimumCalculatedMin
    }
  }
}
